import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const STORAGE_TOKEN_KEY = 'youtubeAuthToken';
const STORAGE_EMAIL_KEY = 'youtubeUserEmail';
const STORAGE_LANGUAGE_KEY = 'youtubeContentLanguage';
const STORAGE_THEME_KEY = 'youtubeThemePreference';
const STORAGE_VISUAL_JOB_KEY = 'youtubeActiveVisualJob';
const STORAGE_AUTOMATION_JOB_KEY = 'youtubeActiveAutomationJob';
const STORAGE_WORKSPACE_KEY = 'youtubeWorkspace';
const JOB_POLL_INTERVAL_MS = 4000;

const VOICES_BY_GENDER = {
  male: ['George', 'Adam', 'Antoni', 'Josh', 'Arnold'],
  female: ['Rachel', 'Bella', 'Domi', 'Elli', 'Nicole'],
};

const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

const SunIcon = ({ className = 'h-4 w-4' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M12 3v2m0 14v2m9-9h-2M5 12H3m15.364 6.364-1.414-1.414M7.05 7.05 5.636 5.636m12.728 0-1.414 1.414M7.05 16.95 5.636 18.364M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" />
  </svg>
);

const MoonIcon = ({ className = 'h-4 w-4' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M21 12.79A9 9 0 0 1 11.21 3 7 7 0 1 0 21 12.79Z" />
  </svg>
);

const readStorage = (key) => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.localStorage.getItem(key) || '';
};

const writeStorage = (key, value) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(key, value);
};

const removeStorage = (key) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(key);
};

const readWorkspace = () => {
  try {
    const raw = readStorage(STORAGE_WORKSPACE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

function App() {
  const [authToken, setAuthToken] = useState(() => readStorage(STORAGE_TOKEN_KEY));
  const [userEmail, setUserEmail] = useState(() => readStorage(STORAGE_EMAIL_KEY));
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState(() => ({
    email: readStorage(STORAGE_EMAIL_KEY),
    password: '',
    confirmPassword: '',
  }));
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');

  const initialWorkspace = readWorkspace();
  const [currentTopic, setCurrentTopic] = useState(initialWorkspace.currentTopic || '');
  const [scenario, setScenario] = useState(initialWorkspace.scenario || '');
  const [scenes, setScenes] = useState(initialWorkspace.scenes || null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreatingVideo, setIsCreatingVideo] = useState(false);
  const [isCreatingVoice, setIsCreatingVoice] = useState(false);
  const [isPreparingVideo, setIsPreparingVideo] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [videoPreview, setVideoPreview] = useState(initialWorkspace.videoPreview || null);
  const [audioPreview, setAudioPreview] = useState(initialWorkspace.audioPreview || null);
  const [recentGenerations, setRecentGenerations] = useState([]);
  const [videoLengthMinutes, setVideoLengthMinutes] = useState(1);
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [voiceGender, setVoiceGender] = useState('male');
  const [selectedVoice, setSelectedVoice] = useState('George');
  const [language, setLanguage] = useState(() => readStorage(STORAGE_LANGUAGE_KEY) || 'tr');
  const [theme, setTheme] = useState(() => readStorage(STORAGE_THEME_KEY) || 'dark');
  const [finalVideoPreview, setFinalVideoPreview] = useState(initialWorkspace.finalVideoPreview || null);
  const [currentDraftId, setCurrentDraftId] = useState(initialWorkspace.currentDraftId || null);
  const [youtubeApiKey, setYoutubeApiKey] = useState('');
  const [topicConcept, setTopicConcept] = useState('');
  const [platform, setPlatform] = useState('youtube');
  const platformOptions = [
    { code: 'youtube', label: 'YouTube' },
    { code: 'twitter', label: 'Twitter' },
    { code: 'instagram', label: 'Instagram' },
    { code: 'tiktok', label: 'TikTok' },
  ];
  const [activeTab, setActiveTab] = useState('automation');
  const [presets, setPresets] = useState([]);
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);
  const [presetName, setPresetName] = useState('');
  const defaultApiKeys = { youtube: '', twitter: '', instagram: '', tiktok: '' };
  const [apiKeys, setApiKeys] = useState({ ...defaultApiKeys });
  const [isSavingApiKeys, setIsSavingApiKeys] = useState(false);
  const [isSavingPreset, setIsSavingPreset] = useState(false);
  const platformUploadLabels = {
    youtube: { card: 'YouTube Yüklemesi', button: "YouTube'a Yükle" },
    twitter: { card: 'Twitter Yüklemesi', button: "Twitter'a Yükle" },
    instagram: { card: 'Instagram Yüklemesi', button: "Instagram'a Yükle" },
    tiktok: { card: 'TikTok Yüklemesi', button: "TikTok'a Yükle" },
  };

  const isDark = theme === 'dark';
  const themeWrapperClass = isDark ? 'theme-dark' : 'theme-light';
  const themeOptions = [
    { code: 'light', label: 'Açık tema', Icon: SunIcon },
    { code: 'dark', label: 'Koyu tema', Icon: MoonIcon },
  ];
  const dashboardTabs = [
    { id: 'automation', label: 'Otomasyon' },
    { id: 'presets', label: 'Kayıtlı Ayarlar' },
    { id: 'account', label: 'Hesap Ayarları' },
  ];

  const ThemeToggle = ({ compact = false }) => (
    <div
      className={combineClasses(
        'inline-flex items-center gap-1 rounded-full border transition-colors duration-200',
        compact ? 'px-1 py-1' : 'px-2 py-1.5',
        isDark ? 'border-slate-700 bg-slate-900/80 text-slate-100' : 'border-slate-200 bg-white/80 text-slate-700 shadow-sm'
      )}
    >
      {themeOptions.map(({ code, label, Icon }) => {
        const active = theme === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => setTheme(code)}
            className={combineClasses(
              'flex items-center justify-center rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40',
              compact ? 'h-8 w-8' : 'h-9 w-9',
              active
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/40'
                : isDark
                ? 'text-slate-300 hover:bg-slate-800/70'
                : 'text-slate-500 hover:bg-slate-100'
            )}
            aria-label={label}
            title={label}
          >
            <Icon className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
          </button>
        );
      })}
    </div>
  );

  const pageBackgroundClass = combineClasses(
    'relative min-h-screen overflow-hidden transition-colors duration-300',
    isDark ? 'bg-slate-950 text-slate-100' : 'bg-slate-100 text-slate-900'
  );
  const gradientBackdropClass = combineClasses(
    'absolute inset-0 -z-10 bg-gradient-to-br transition-colors duration-500',
    isDark ? 'from-slate-950 via-slate-900 to-slate-950' : 'from-white via-slate-100 to-slate-200'
  );
  const primaryBubbleClass = isDark ? 'bg-blue-500/25' : 'bg-blue-400/25';
  const secondaryBubbleClass = isDark ? 'bg-purple-500/20' : 'bg-purple-400/25';
  const panelClass = combineClasses(
    'rounded-3xl border transition-all duration-300',
    isDark
      ? 'border-slate-800/60 bg-slate-900/70 text-slate-100 shadow-2xl shadow-slate-900/40'
      : 'border-slate-200 bg-white text-slate-900 shadow-xl shadow-slate-200/70'
  );
  const compactPanelClass = combineClasses(
    'rounded-2xl border transition-all duration-300',
    isDark
      ? 'border-slate-800/60 bg-slate-900/60 text-slate-200 shadow-lg shadow-slate-900/30'
      : 'border-slate-200 bg-white/95 text-slate-700 shadow-lg shadow-slate-200/60'
  );
  const mutedSurfaceClass = combineClasses(
    'rounded-2xl border transition-all duration-300',
    isDark ? 'border-slate-800/70 bg-slate-950/60 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600'
  );
  const headerSurfaceClass = combineClasses(
    'border-b backdrop-blur transition-colors duration-300',
    isDark ? 'border-slate-800/60 bg-slate-950/70 text-slate-100' : 'border-slate-200 bg-white/90 text-slate-900 shadow-sm'
  );
  const statusBannerClass = combineClasses(
    'rounded-2xl border px-5 py-4 text-sm transition-all duration-300',
    isDark
      ? 'border-blue-500/30 bg-blue-500/10 text-blue-100 shadow-lg shadow-blue-900/20'
      : 'border-blue-200 bg-blue-50 text-blue-700 shadow-sm'
  );
  const pillClass = combineClasses(
    'inline-flex items-center rounded-full border px-4 py-1 text-sm font-medium tracking-wide transition-colors duration-300',
    isDark ? 'border-slate-700 bg-slate-900/70 text-slate-200 shadow shadow-slate-900/30' : 'border-slate-200 bg-white/80 text-slate-600 shadow-sm'
  );
  const inputClass = combineClasses(
    'mt-2 w-full rounded-2xl border px-4 py-3 shadow-inner transition-colors duration-200 focus:outline-none focus:ring-2',
    isDark
      ? 'border-slate-700/70 bg-slate-900/70 text-slate-100 placeholder-slate-500 focus:border-blue-500 focus:ring-blue-500/40'
      : 'border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/30'
  );
  const textAreaClass = combineClasses(
    'w-full rounded-2xl border px-4 py-3 shadow-inner transition-colors duration-200 focus:outline-none focus:ring-2',
    isDark
      ? 'border-slate-700/70 bg-slate-900/70 text-slate-100 placeholder-slate-500 focus:border-emerald-500 focus:ring-emerald-500/40'
      : 'border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:ring-emerald-500/30'
  );
  const subtleButtonClass = combineClasses(
    'rounded-xl px-4 py-2 text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30',
    isDark ? 'border border-slate-700/70 bg-transparent text-slate-200 hover:bg-slate-800/70' : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
  );
  const logoutButtonClass = combineClasses(
    'rounded-xl px-4 py-2 text-sm font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500/30',
    isDark
      ? 'bg-slate-800 text-slate-100 hover:bg-slate-700'
      : 'bg-white text-slate-900 border border-slate-300 hover:bg-slate-100'
  );

  const hasTopic = currentTopic.trim().length > 0;
  const hasScenario = scenario.trim().length > 0;

  const isAuthenticated = Boolean(authToken);

  const clearAuth = (message = '') => {
    setAuthToken('');
    setUserEmail('');
    removeStorage(STORAGE_TOKEN_KEY);
    removeStorage(STORAGE_EMAIL_KEY);
    setAuthForm({ email: '', password: '', confirmPassword: '' });
    setAuthMode('login');
    setAuthSuccess('');
    if (message) {
      setAuthError(message);
    } else {
      setAuthError('');
    }
  };

  const handleUnauthorized = (message) => {
    clearAuth(message);
    setGenerationStatus('');
    setRecentGenerations([]);
  };

  const finalizeAuth = (token, email) => {
    setAuthToken(token);
    setUserEmail(email);
    writeStorage(STORAGE_TOKEN_KEY, token);
    writeStorage(STORAGE_EMAIL_KEY, email);
    setAuthForm({ email, password: '', confirmPassword: '' });
    setAuthError('');
    setAuthSuccess('');
  };

  const extractErrorMessage = (error, fallback) => {
    if (error?.response?.status === 401) {
      if (authToken) {
        handleUnauthorized('Oturum süreniz doldu. Lütfen tekrar giriş yapın.');
      }
      return 'Yetkilendirme başarısız. Lütfen tekrar giriş yapın.';
    }

    const detail = error?.response?.data?.detail;
    if (detail) {
      return Array.isArray(detail) ? detail[0]?.msg || fallback : detail;
    }

    if (error?.message) {
      return error.message;
    }

    return fallback;
  };

  const loadGenerationToForm = (generation) => {
    if (!generation) return;
    skipLanguageResetRef.current = true;
    const normalizePath = (p) => (p && !p.startsWith('/') ? `/${p}` : p);

    setCurrentTopic(generation.topic || '');
    setScenario(generation.scenario || '');
    setScenes(generation.scenes || null);
    setCurrentDraftId(generation.id || null);

    setVideoPreview(normalizePath(generation.video_path) || null);
    setFinalVideoPreview(normalizePath(generation.video_path) || null);
    setAudioPreview(normalizePath(generation.audio_path) || null);

    if (generation.platform) setPlatform(generation.platform);
    if (generation.aspect_ratio) setAspectRatio(generation.aspect_ratio);
    if (generation.language) setLanguage(generation.language);
    if (generation.duration_minutes) setVideoLengthMinutes(generation.duration_minutes);
    if (generation.voice) {
      setSelectedVoice(generation.voice);
      if (VOICES_BY_GENDER.female.includes(generation.voice)) setVoiceGender('female');
      else if (VOICES_BY_GENDER.male.includes(generation.voice)) setVoiceGender('male');
    }

    setActiveTab('automation');
    setGenerationStatus(`"${generation.topic}" yüklendi.`);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pollJob = (jobId, { onProgress, onDone, onFail }) => {
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        const response = await axios.get(`/api/jobs/${jobId}`);
        const job = response.data;
        if (!job) return;

        if (job.status === 'pending' || job.status === 'running') {
          if (onProgress) onProgress(job.progress || 'İşleniyor...');
          setTimeout(tick, JOB_POLL_INTERVAL_MS);
        } else if (job.status === 'done') {
          if (onDone) onDone(job.result);
        } else if (job.status === 'failed') {
          if (onFail) onFail(job.error || 'İşlem başarısız oldu.');
        }
      } catch (error) {
        if (error?.response?.status === 404) {
          if (onFail) onFail('İşlem bulunamadı veya süresi doldu.');
          return;
        }
        if (onFail) onFail(extractErrorMessage(error, 'Durum sorgulanamadı.'));
      }
    };

    tick();
    return () => {
      cancelled = true;
    };
  };

  async function fetchRecentGenerations() {
    if (!authToken) {
      return;
    }

    try {
      const response = await axios.get('/api/generations');
      setRecentGenerations(response.data);
    } catch (error) {
      const message = extractErrorMessage(error, 'Son üretimler yüklenemedi.');
      setGenerationStatus(message);
    }
  }

  const fetchApiKeys = async () => {
    if (!authToken) {
      return;
    }
    try {
      const response = await axios.get('/api/user/api-keys');
      const keys = response.data?.keys || {};
      const mergedKeys = {
        ...defaultApiKeys,
        ...Object.fromEntries(
          Object.entries(keys).map(([key, value]) => [key, value || ''])
        ),
      };
      setApiKeys(mergedKeys);
      setYoutubeApiKey(mergedKeys.youtube || '');
    } catch (error) {
      const message = extractErrorMessage(error, 'API anahtarları yüklenemedi.');
      setGenerationStatus(message);
    }
  };

  const fetchPresets = async () => {
    if (!authToken) {
      return;
    }
    setIsLoadingPresets(true);
    try {
      const response = await axios.get('/api/user/presets');
      setPresets(response.data?.presets || []);
    } catch (error) {
      const message = extractErrorMessage(error, 'Kayıtlı ayarlar yüklenemedi.');
      setGenerationStatus(message);
    } finally {
      setIsLoadingPresets(false);
    }
  };

  useEffect(() => {
    if (authToken) {
      axios.defaults.headers.common.Authorization = `Bearer ${authToken}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  }, [authToken]);

  useEffect(() => {
    if (!authToken) {
      setRecentGenerations([]);
      return;
    }

    fetchRecentGenerations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    if (!authToken) {
    setApiKeys({ ...defaultApiKeys });
      setPresets([]);
      setYoutubeApiKey('');
      return;
    }
    fetchApiKeys();
    fetchPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const skipLanguageResetRef = useRef(true);
  useEffect(() => {
    writeStorage(STORAGE_LANGUAGE_KEY, language);
    if (skipLanguageResetRef.current) {
      skipLanguageResetRef.current = false;
      return;
    }
    setCurrentTopic('');
    setScenario('');
    setScenes(null);
    setVideoPreview(null);
    setAudioPreview(null);
    setFinalVideoPreview(null);
    setCurrentDraftId(null);
  }, [language]);

  useEffect(() => {
    writeStorage(STORAGE_THEME_KEY, theme);
    if (typeof document !== 'undefined') {
      document.documentElement.classList.toggle('dark', theme === 'dark');
      document.body.classList.toggle('theme-dark', theme === 'dark');
      document.body.classList.toggle('theme-light', theme !== 'dark');
    }
  }, [theme]);

  useEffect(() => {
    setTopicConcept('');
  }, [platform]);

  useEffect(() => {
    const snapshot = {
      currentTopic,
      scenario,
      scenes,
      videoPreview,
      audioPreview,
      finalVideoPreview,
      currentDraftId,
    };
    writeStorage(STORAGE_WORKSPACE_KEY, JSON.stringify(snapshot));
  }, [currentTopic, scenario, scenes, videoPreview, audioPreview, finalVideoPreview, currentDraftId]);

  useEffect(() => {
    if (!authToken) return;

    const visualJobId = readStorage(STORAGE_VISUAL_JOB_KEY);
    if (visualJobId) {
      setIsCreatingVideo(true);
      setGenerationStatus('Görsel üretimi devam ediyor, durum kontrol ediliyor...');
      pollJob(visualJobId, {
        onProgress: (msg) => setGenerationStatus(msg),
        onDone: (result) => {
          removeStorage(STORAGE_VISUAL_JOB_KEY);
          setVideoPreview(result?.video_path || null);
          setGenerationStatus('Görseller hazır!');
          fetchRecentGenerations();
          setIsCreatingVideo(false);
        },
        onFail: (errMsg) => {
          removeStorage(STORAGE_VISUAL_JOB_KEY);
          setGenerationStatus(errMsg);
          setIsCreatingVideo(false);
        },
      });
    }

    const automationJobId = readStorage(STORAGE_AUTOMATION_JOB_KEY);
    if (automationJobId) {
      setIsGenerating(true);
      setGenerationStatus('Otomasyon devam ediyor, durum kontrol ediliyor...');
      pollJob(automationJobId, {
        onProgress: (msg) => setGenerationStatus(msg),
        onDone: (result) => {
          removeStorage(STORAGE_AUTOMATION_JOB_KEY);
          if (result?.video_path) setFinalVideoPreview(result.video_path);
          setGenerationStatus('Tam otomasyon tamamlandı!');
          fetchRecentGenerations();
          setIsGenerating(false);
        },
        onFail: (errMsg) => {
          removeStorage(STORAGE_AUTOMATION_JOB_KEY);
          setGenerationStatus(errMsg);
          setIsGenerating(false);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const handleAuthInputChange = (event) => {
    const { name, value } = event.target;
    setAuthForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleAuthSubmit = async (event) => {
    event.preventDefault();
    if (authLoading) {
      return;
    }

    if (!authForm.email.trim() || !authForm.password.trim()) {
      setAuthError('E-posta ve şifre zorunludur.');
      return;
    }

    if (authMode === 'register') {
      if (authForm.password.length < 6) {
        setAuthError('Şifreniz en az 6 karakter olmalıdır.');
        return;
      }
      if (authForm.password !== authForm.confirmPassword) {
        setAuthError('Şifreler eşleşmiyor.');
        return;
      }
    }

    setAuthLoading(true);
    setAuthError('');
    setAuthSuccess('');

    try {
      if (authMode === 'register') {
        await axios.post('/api/auth/register', {
          email: authForm.email.trim(),
          password: authForm.password,
        });
        const loginResponse = await axios.post('/api/auth/login', {
          email: authForm.email.trim(),
          password: authForm.password,
        });
        finalizeAuth(loginResponse.data.access_token, authForm.email.trim());
        setGenerationStatus('Hoş geldiniz! Birlikte etkileyici videolar üretelim.');
      } else {
        const response = await axios.post('/api/auth/login', {
          email: authForm.email.trim(),
          password: authForm.password,
        });
        finalizeAuth(response.data.access_token, authForm.email.trim());
        setGenerationStatus('Tekrar hoş geldiniz! Hazırsanız üretime başlayalım.');
      }
    } catch (error) {
      const message = extractErrorMessage(
        error,
        authMode === 'register'
          ? 'Üyelik oluşturulamadı. Lütfen tekrar deneyin.'
          : 'Giriş yapılamadı. Lütfen bilgilerinizi kontrol edin.'
      );
      setAuthError(message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    clearAuth('');
    setScenario('');
    setScenes(null);
    setCurrentTopic('');
    setVideoPreview(null);
    setAudioPreview(null);
    setFinalVideoPreview(null);
    setYoutubeApiKey('');
    setGenerationStatus('');
    setPlatform('youtube');
    setPresets([]);
    setApiKeys(defaultApiKeys);
    setPresetName('');
    setActiveTab('automation');
  };

  const autoGenerateTopic = async () => {
    try {
      setGenerationStatus('Trend konular taranıyor...');
      const response = await axios.get('/api/trending-topic', {
        params: { language, platform, concept: topicConcept },
      });
      setCurrentTopic(response.data.topic);
      setScenario('');
    setScenes(null);
      setGenerationStatus('Yeni bir konu bulundu!');
    } catch (error) {
      const message = extractErrorMessage(error, 'Konu bulunurken hata oluştu.');
      setGenerationStatus(message);
    }
  };

  const generateScenario = async () => {
    if (!hasTopic) {
      setGenerationStatus('Lütfen önce bir konu seçin.');
      return;
    }

    try {
      setGenerationStatus('Senaryo yazılıyor...');
      const response = await axios.post('/api/generate-scenario', {
        topic: currentTopic,
        duration_minutes: Number(videoLengthMinutes) || 1,
        language,
        platform,
      });
      setScenario(response.data.scenario);
      setScenes(response.data.scenes || null);
      setCurrentDraftId(response.data.draft_id || null);
      setVideoPreview(null);
      setAudioPreview(null);
      setFinalVideoPreview(null);
      setGenerationStatus('Senaryo hazır!');
      fetchRecentGenerations();
    } catch (error) {
      const message = extractErrorMessage(error, 'Senaryo oluşturulamadı.');
      setGenerationStatus(message);
    }
  };

  const createVisuals = async () => {
    if (!hasScenario) {
      setGenerationStatus('Lütfen önce bir senaryo oluşturun.');
      return;
    }

    setIsCreatingVideo(true);
    setGenerationStatus('Görseller hazırlanıyor...');

    try {
      const response = await axios.post('/api/create-visuals', {
        topic: currentTopic,
        scenario,
        scenes: scenes || undefined,
        duration_minutes: Number(videoLengthMinutes) || 1,
        aspect_ratio: aspectRatio,
        language,
        platform,
        draft_id: currentDraftId,
      });

      const jobId = response.data?.job_id;
      if (!jobId) {
        throw new Error('Sunucu job_id döndürmedi.');
      }
      if (response.data?.draft_id) setCurrentDraftId(response.data.draft_id);
      writeStorage(STORAGE_VISUAL_JOB_KEY, jobId);

      pollJob(jobId, {
        onProgress: (msg) => setGenerationStatus(msg),
        onDone: (result) => {
          removeStorage(STORAGE_VISUAL_JOB_KEY);
          if (result?.draft_id) setCurrentDraftId(result.draft_id);
          setVideoPreview(result?.video_path || null);
          setFinalVideoPreview(null);
          setGenerationStatus('Görseller hazır. Sırada seslendirme var!');
          fetchRecentGenerations();
          setIsCreatingVideo(false);
        },
        onFail: (errMsg) => {
          removeStorage(STORAGE_VISUAL_JOB_KEY);
          setGenerationStatus(errMsg);
          setIsCreatingVideo(false);
        },
      });
    } catch (error) {
      const message = extractErrorMessage(error, 'Görseller oluşturulamadı.');
      setGenerationStatus(message);
      setIsCreatingVideo(false);
    }
  };

  const createVoice = async () => {
    if (!hasScenario) {
      setGenerationStatus('Lütfen önce bir senaryo oluşturun.');
      return;
    }

    setIsCreatingVoice(true);
    setGenerationStatus('Seslendirme hazırlanıyor...');

    try {
      const response = await axios.post('/api/create-voice', {
        scenario,
        topic: currentTopic,
        duration_minutes: Number(videoLengthMinutes) || 1,
        language,
        platform,
        voice: selectedVoice,
        draft_id: currentDraftId,
      });
      if (response.data?.draft_id) setCurrentDraftId(response.data.draft_id);

      setAudioPreview(response.data.audio_path);
      setGenerationStatus('Seslendirme tamamlandı!');
      setFinalVideoPreview(null);
    } catch (error) {
      const message = extractErrorMessage(error, 'Seslendirme oluşturulamadı.');
      setGenerationStatus(message);
    } finally {
      setIsCreatingVoice(false);
    }
  };

  const prepareFullVideo = async () => {
    if (!videoPreview || !audioPreview) {
      setGenerationStatus('Videoyu hazırlamak için önce görsel ve ses üretmelisiniz.');
      return;
    }

    setIsPreparingVideo(true);
    setGenerationStatus('Final video hazırlanıyor...');

    try {
      const response = await axios.post('/api/prepare-video', {
        video_path: videoPreview,
        audio_path: audioPreview,
        scenario,
        topic: currentTopic,
        scenes: scenes || undefined,
        duration_minutes: Number(videoLengthMinutes) || 1,
        aspect_ratio: aspectRatio,
        platform,
        draft_id: currentDraftId,
      });
      if (response.data?.draft_id) setCurrentDraftId(response.data.draft_id);

      const preparedPath = response.data.video_path || response.data.final_video_path;
      if (preparedPath) {
        setFinalVideoPreview(preparedPath);
        setGenerationStatus('Final video hazır!');
        fetchRecentGenerations();
      } else {
        setGenerationStatus('Final video yolu alınamadı.');
      }
    } catch (error) {
      const message = extractErrorMessage(error, 'Final video hazırlanamadı.');
      setGenerationStatus(message);
    } finally {
      setIsPreparingVideo(false);
    }
  };

  const uploadToYouTube = async () => {
    if (!(finalVideoPreview || videoPreview)) {
      setGenerationStatus('Yüklemek için kullanıma hazır bir video bulunamadı.');
      return;
    }

    if (!youtubeApiKey.trim()) {
      setGenerationStatus('YouTube API anahtarı gereklidir.');
      return;
    }

    setIsGenerating(true);
    setGenerationStatus('YouTube yüklemesi başlatıldı...');

    try {
      const response = await axios.post('/api/upload-youtube', {
        video_path: finalVideoPreview || videoPreview,
        topic: currentTopic,
        scenario,
        youtube_api_key: youtubeApiKey.trim(),
        platform,
      });

      if (response.data?.youtube_url) {
        setGenerationStatus('Video YouTube kanalınıza gönderildi!');
      } else {
        setGenerationStatus('Video yüklendi ancak bağlantı alınamadı.');
      }
      fetchRecentGenerations();
    } catch (error) {
      const message = extractErrorMessage(error, 'Video YouTube\'a yüklenemedi.');
      setGenerationStatus(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const runAutomation = async () => {
    if (!topicConcept.trim()) {
      setGenerationStatus('Lütfen önce konsept/tema girin.');
      return;
    }
    setIsGenerating(true);
    setGenerationStatus('Tam otomasyon başlatılıyor...');

    try {
      const response = await axios.post('/api/run-automation', {
        duration_minutes: Number(videoLengthMinutes) || 1,
        aspect_ratio: aspectRatio,
        language,
        platform,
        concept: topicConcept,
      });

      const jobId = response.data?.job_id;
      if (!jobId) {
        throw new Error('Sunucu job_id döndürmedi.');
      }
      writeStorage(STORAGE_AUTOMATION_JOB_KEY, jobId);

      pollJob(jobId, {
        onProgress: (msg) => setGenerationStatus(msg),
        onDone: (result) => {
          removeStorage(STORAGE_AUTOMATION_JOB_KEY);
          if (result?.video_path) setFinalVideoPreview(result.video_path);
          setGenerationStatus('Tam otomasyon tamamlandı!');
          fetchRecentGenerations();
          setIsGenerating(false);
        },
        onFail: (errMsg) => {
          removeStorage(STORAGE_AUTOMATION_JOB_KEY);
          setGenerationStatus(errMsg);
          setIsGenerating(false);
        },
      });
    } catch (error) {
      const message = extractErrorMessage(error, 'Otomasyon tamamlanamadı.');
      setGenerationStatus(message);
      setIsGenerating(false);
    }
  };

  const handleSaveApiKeys = async () => {
    setIsSavingApiKeys(true);
    try {
      await axios.post('/api/user/api-keys', {
        keys: apiKeys,
      });
      setGenerationStatus('API anahtarları kaydedildi.');
      fetchApiKeys();
    } catch (error) {
      const message = extractErrorMessage(error, 'API anahtarları kaydedilemedi.');
      setGenerationStatus(message);
    } finally {
      setIsSavingApiKeys(false);
    }
  };

  const handlePresetSave = async () => {
    const trimmedName = presetName.trim();
    if (!trimmedName) {
      setGenerationStatus('Lütfen kayıtlı ayar için bir ad girin.');
      return;
    }

    setIsSavingPreset(true);
    try {
      await axios.post('/api/user/presets', {
        name: trimmedName,
        platform,
        duration_minutes: Number(videoLengthMinutes) || 1,
        aspect_ratio: aspectRatio,
        language,
        auto_upload: false,
      });
      setPresetName('');
      setGenerationStatus('Ayar başarıyla kaydedildi.');
      fetchPresets();
    } catch (error) {
      const message = extractErrorMessage(error, 'Ayar kaydedilemedi.');
      setGenerationStatus(message);
    } finally {
      setIsSavingPreset(false);
    }
  };

  const handlePresetDelete = async (presetId) => {
    try {
      await axios.delete(`/api/user/presets/${presetId}`);
      setGenerationStatus('Ayar silindi.');
      fetchPresets();
    } catch (error) {
      const message = extractErrorMessage(error, 'Ayar silinemedi.');
      setGenerationStatus(message);
    }
  };

  const handlePresetRun = async (preset) => {
    if (preset.platform) {
      setPlatform(preset.platform);
    }
    if (preset.aspect_ratio) {
      setAspectRatio(preset.aspect_ratio);
    }
    if (preset.duration_minutes) {
      setVideoLengthMinutes(String(preset.duration_minutes));
    }
    if (preset.language) {
      setLanguage(preset.language);
    }
    setIsGenerating(true);
    setGenerationStatus(`"${preset.name}" ayarı çalıştırılıyor...`);
    try {
      const response = await axios.post(`/api/user/presets/${preset.id}/run`);
      const result = response.data || {};
      if (result.topic) {
        setCurrentTopic(result.topic);
      }
      if (result.scenario) {
        setScenario(result.scenario);
      }
      if (result.platform) {
        setPlatform(result.platform);
      }
      if (result.aspect_ratio) {
        setAspectRatio(result.aspect_ratio);
      }
      setVideoPreview(result.video_path || null);
      setAudioPreview(result.audio_path || null);
      setFinalVideoPreview(result.video_path || null);
      setGenerationStatus(`"${preset.name}" için otomasyon tamamlandı.`);
      fetchRecentGenerations();
      setActiveTab('automation');
    } catch (error) {
      const message = extractErrorMessage(error, 'Kayıtlı ayar çalıştırılamadı.');
      setGenerationStatus(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const hasAnyYoutubeKey = Boolean(youtubeApiKey.trim() || apiKeys.youtube);
  const assetsReady = [
    videoPreview ? 'Görsel hazır' : null,
    audioPreview ? 'Ses hazır' : null,
    finalVideoPreview ? 'Final video hazır' : null,
  ].filter(Boolean);

  const scenarioLength = scenario ? scenario.trim().split(/\s+/).length : 0;

  if (!isAuthenticated) {
    return (
      <div className={`app-theme ${themeWrapperClass}`}>
        <div className={pageBackgroundClass}>
          <div className="absolute right-6 top-6 z-20 flex items-center justify-end">
            <ThemeToggle compact />
          </div>

          <div className={`${gradientBackdropClass} login-gradient`} />
          <div
            className={combineClasses(
              'absolute top-24 -left-16 h-72 w-72 rounded-full blur-3xl login-bubble',
              primaryBubbleClass
            )}
          />
          <div
            className={combineClasses(
              'absolute bottom-10 right-10 h-96 w-96 rounded-full blur-3xl login-bubble',
              secondaryBubbleClass
            )}
          />

          <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl flex-col justify-center px-6 py-16">
            <div className="grid gap-16 md:grid-cols-5 md:items-center">
              <div className="space-y-6 md:col-span-3">
                <div className={combineClasses(pillClass, 'badge-pill')}>
                  YouTube Automation Studio
                </div>
                <h1
                  className={combineClasses(
                    'text-4xl font-semibold leading-tight transition-colors duration-300 md:text-5xl',
                    isDark ? 'text-white' : 'text-slate-900 hero-title'
                  )}
                >
                  Profesyonel YouTube içeriklerini dakikalar içinde hazırlayın.
                </h1>
                <p
                  className={combineClasses(
                    'text-lg md:max-w-xl transition-colors duration-300 hero-subtitle',
                    isDark ? 'text-slate-300' : 'text-slate-600'
                  )}
                >
                  Yapay zeka destekli senaryo, görsel, seslendirme ve yükleme araçları tek platformda.
                  Otomasyon akışını yönetin, içerikleri hızla üretin ve kanalınızı büyütün.
                </p>

              <div className="grid gap-4 sm:grid-cols-2">
                  <div className={combineClasses(compactPanelClass, 'p-5 feature-card')}>
                    <p className="text-sm font-medium">Otomatik İçerik Akışı</p>
                    <p className={combineClasses('mt-2 text-2xl font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                      4 adımda üretim
                    </p>
                    <p className={combineClasses('mt-2 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                      Konu, senaryo, görsel ve seslendirmeyi tek tuşla yönetin.
                    </p>
                  </div>
                  <div className={combineClasses(compactPanelClass, 'p-5 feature-card')}>
                    <p className="text-sm font-medium">İçerik Takibi</p>
                    <p className={combineClasses('mt-2 text-2xl font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                      Son üretimleri görün
                    </p>
                    <p className={combineClasses('mt-2 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                      Önceki otomasyonlarınızı ve sonuçlarını anında listeleyin.
                    </p>
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleAuthSubmit}
                className={combineClasses(
                  panelClass,
                  'md:col-span-2 space-y-6 p-8 backdrop-blur-sm auth-card'
                )}
              >
                <div
                  className={combineClasses(
                    'flex gap-2 rounded-full p-1 text-sm font-medium auth-switcher transition-colors duration-300',
                    isDark ? 'bg-slate-800/60 text-slate-300' : 'bg-slate-100 text-slate-600'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setAuthMode('login');
                      setAuthError('');
                      setAuthSuccess('');
                    }}
                    className={`flex-1 rounded-full px-4 py-2 transition ${
                      authMode === 'login'
                        ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/40'
                        : 'hover:text-white'
                    }`}
                  >
                    Giriş Yap
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthMode('register');
                      setAuthError('');
                      setAuthSuccess('');
                    }}
                    className={`flex-1 rounded-full px-4 py-2 transition ${
                      authMode === 'register'
                        ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/40'
                        : 'hover:text-white'
                    }`}
                  >
                    Üye Ol
                  </button>
                </div>

                <div>
                  <label
                    className={combineClasses('text-sm font-medium transition-colors duration-300', isDark ? 'text-slate-300' : 'text-slate-600')}
                    htmlFor="email"
                  >
                    E-posta adresi
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={authForm.email}
                    onChange={handleAuthInputChange}
                    className={inputClass}
                    placeholder="ornek@firma.com"
                  />
                </div>

                <div>
                  <label
                    className={combineClasses('text-sm font-medium transition-colors duration-300', isDark ? 'text-slate-300' : 'text-slate-600')}
                    htmlFor="password"
                  >
                    Şifre
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete={authMode === 'register' ? 'new-password' : 'current-password'}
                    value={authForm.password}
                    onChange={handleAuthInputChange}
                    className={inputClass}
                    placeholder="••••••••"
                  />
                </div>

                {authMode === 'register' && (
                  <div>
                    <label
                      className={combineClasses('text-sm font-medium transition-colors duration-300', isDark ? 'text-slate-300' : 'text-slate-600')}
                      htmlFor="confirmPassword"
                    >
                    Şifre (tekrar)
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    value={authForm.confirmPassword}
                    onChange={handleAuthInputChange}
                    className={combineClasses(
                      inputClass,
                      isDark
                        ? 'focus:border-purple-500 focus:ring-purple-500/40'
                        : 'focus:border-purple-500 focus:ring-purple-500/30'
                    )}
                    placeholder="••••••••"
                  />
                </div>
              )}

              {authError && (
                <div className="rounded-2xl border border-red-500/40 bg-red-500/15 px-4 py-3 text-sm text-red-200">
                  {authError}
                </div>
              )}

              {authSuccess && (
                <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/15 px-4 py-3 text-sm text-emerald-200">
                  {authSuccess}
                </div>
              )}

              <button
                type="submit"
                className="w-full rounded-2xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 px-6 py-3 text-center text-sm font-semibold uppercase tracking-wide text-white shadow-lg shadow-blue-900/30 transition hover:shadow-emerald-900/40 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={authLoading}
              >
                {authLoading
                  ? 'İşleniyor...'
                  : authMode === 'login'
                  ? 'Giriş Yap'
                  : 'Üyeliğini Başlat'}
              </button>

              <p className={combineClasses('text-xs transition-colors duration-300', isDark ? 'text-slate-500' : 'text-slate-400')}>
                Giriş yaparak platform kullanım koşullarını ve gizlilik sözleşmesini kabul etmiş olursunuz.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

  return (
    <div className={`app-theme ${themeWrapperClass}`}>
      <div className={pageBackgroundClass}>
        <div className={`${gradientBackdropClass} dashboard-gradient`} />
        <div
          className={combineClasses(
            'absolute top-20 left-10 -z-10 h-56 w-56 rounded-full blur-3xl dashboard-bubble',
            primaryBubbleClass
          )}
        />
        <div
          className={combineClasses(
            'absolute bottom-24 right-16 -z-10 h-72 w-72 rounded-full blur-3xl dashboard-bubble',
            secondaryBubbleClass
          )}
        />

        <header className={combineClasses(headerSurfaceClass, 'dashboard-header')}>
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-5">
            <div>
              <p className={combineClasses('text-xs font-medium uppercase tracking-[0.4em] transition-colors duration-300', isDark ? 'text-blue-300' : 'text-blue-500')}>
                Dashboard
              </p>
              <h1
                className={combineClasses(
                  'mt-1 text-2xl font-semibold transition-colors duration-300 dashboard-title',
                  isDark ? 'text-white' : 'text-slate-900'
                )}
              >
                YouTube Automation Studio
              </h1>
              <p className={combineClasses('text-xs transition-colors duration-300', isDark ? 'text-slate-400' : 'text-slate-500')}>
                Oturum: {userEmail}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <ThemeToggle />
              <button onClick={fetchRecentGenerations} className={subtleButtonClass}>
                Verileri Yenile
              </button>
              <button onClick={handleLogout} className={logoutButtonClass}>
                Çıkış Yap
              </button>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-6xl space-y-10 px-6 py-10">
          {generationStatus && <div className={statusBannerClass}>{generationStatus}</div>}

          <div className={combineClasses(panelClass, 'px-4 py-3 md:px-6 md:py-4')}
          >
            <div className="flex flex-wrap items-center gap-2">
              {dashboardTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={combineClasses(
                    'rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-500/30',
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/30'
                      : isDark
                      ? 'border border-slate-700/70 text-slate-300 hover:bg-slate-800/70'
                      : 'border border-slate-300 text-slate-600 hover:bg-slate-100'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'automation' && (
            <>
          <section className="grid gap-5 md:grid-cols-3">
            <div className={combineClasses(compactPanelClass, 'p-6')}>
              <p
                className={combineClasses(
                  'text-xs uppercase tracking-wide',
                  isDark ? 'text-slate-400' : 'text-slate-500'
                )}
              >
                Aktif konu
              </p>
              <p className={combineClasses('mt-2 text-lg font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                {currentTopic || 'Henüz konu seçilmedi'}
              </p>
              <p
                className={combineClasses(
                  'mt-3 text-xs',
                  isDark ? 'text-slate-400' : 'text-slate-500'
                )}
              >
                {scenarioLength
                  ? `${scenarioLength} kelimelik senaryo hazır.`
                  : 'Senaryo üretildiğinde burada özetlenecek.'}
              </p>
            </div>
            <div className={combineClasses(compactPanelClass, 'p-6')}>
              <p
                className={combineClasses(
                  'text-xs uppercase tracking-wide',
                  isDark ? 'text-slate-400' : 'text-slate-500'
                )}
              >
                Üretim ayarı
              </p>
              <p className={combineClasses('mt-2 text-lg font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                {videoLengthMinutes || 1} dk • {aspectRatio}
              </p>
              <p
                className={combineClasses(
                  'mt-3 text-xs',
                  isDark ? 'text-slate-400' : 'text-slate-500'
                )}
              >
                Mobil için 9:16, YouTube için 16:9 oranı arasında geçiş yapabilirsiniz.
              </p>
            </div>
            <div className={combineClasses(compactPanelClass, 'p-6')}>
              <p
                className={combineClasses(
                  'text-xs uppercase tracking-wide',
                  isDark ? 'text-slate-400' : 'text-slate-500'
                )}
              >
                Hazır varlıklar
              </p>
              {assetsReady.length > 0 ? (
                <ul className={combineClasses('mt-2 space-y-1 text-sm', isDark ? 'text-emerald-200' : 'text-emerald-600')}>
                  {assetsReady.map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={combineClasses('mt-2 text-lg font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                  Varlık bekleniyor
                </p>
              )}
              <p
                className={combineClasses(
                  'mt-3 text-xs',
                  isDark ? 'text-slate-400' : 'text-slate-500'
                )}
              >
                Görsel, ses ve final video ilerlemesi burada listelenir.
              </p>
            </div>
          </section>
          <section className="grid gap-6 lg:grid-cols-2">
            <div className={combineClasses(panelClass, 'space-y-6 p-6')}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className={combineClasses('text-xl font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                  Konu &amp; Senaryo
                </h2>
                <button
                  onClick={runAutomation}
                  className="rounded-xl bg-purple-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={!authToken || isGenerating || !topicConcept.trim()}
                >
                  Tam Otomasyonu Çalıştır
                </button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label
                    className={combineClasses('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}
                  >
                    Platform
                  </label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {platformOptions.map((item) => {
                      const active = platform === item.code;
                      return (
                        <button
                          key={item.code}
                          type="button"
                          onClick={() => setPlatform(item.code)}
                          className={combineClasses(
                            'rounded-xl px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60',
                            active
                              ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg shadow-purple-500/40'
                              : isDark
                              ? 'border border-slate-700/70 text-slate-300 hover:bg-slate-800/70'
                              : 'border border-slate-300 text-slate-600 hover:bg-slate-100'
                          )}
                          disabled={!authToken}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label
                    className={combineClasses('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}
                  >
                    Konsept / Tema
                  </label>
                  <input
                    type="text"
                    value={topicConcept}
                    onChange={(event) => setTopicConcept(event.target.value)}
                    placeholder="Örn. What if ... / Alternatif tarih / Bilim kurgu"
                    className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
                    disabled={!authToken}
                  />
                </div>

                <div>
                  <label
                    className={combineClasses('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}
                  >
                    Video Süresi (dk)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={videoLengthMinutes}
                    onChange={(event) => setVideoLengthMinutes(event.target.value)}
                    className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
                    disabled={!authToken}
                  />
                </div>

                <div>
                  <label
                    className={combineClasses('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}
                  >
                    Dil
                  </label>
                  <div
                    className={combineClasses(
                      'mt-2 grid grid-cols-2 gap-2 rounded-2xl border p-2 transition-colors duration-200',
                      isDark ? 'border-slate-700/70 bg-slate-900/70' : 'border-slate-300 bg-white'
                    )}
                  >
                    {[
                      { code: 'tr', label: 'Türkçe' },
                      { code: 'en', label: 'English' },
                    ].map((item) => {
                      const active = language === item.code;
                      return (
                        <button
                          key={item.code}
                          type="button"
                          onClick={() => setLanguage(item.code)}
                          className={combineClasses(
                            'rounded-xl px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60',
                            active
                              ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-600/40'
                              : isDark
                              ? 'text-slate-300 hover:bg-slate-800/70 hover:text-white'
                              : 'text-slate-600 hover:bg-slate-100'
                          )}
                          disabled={!authToken}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label
                    className={combineClasses('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}
                  >
                    Görsel Formatı
                  </label>
                  <div
                    className={combineClasses(
                      'mt-2 grid grid-cols-2 gap-2 rounded-2xl border p-2 transition-colors duration-200',
                      isDark ? 'border-slate-700/70 bg-slate-900/70' : 'border-slate-300 bg-white'
                    )}
                  >
                    {['9:16', '16:9'].map((ratio) => {
                      const active = aspectRatio === ratio;
                      return (
                        <button
                          key={ratio}
                          type="button"
                          onClick={() => setAspectRatio(ratio)}
                          className={combineClasses(
                            'rounded-xl px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60',
                            active
                              ? 'bg-blue-500 text-white shadow-lg shadow-blue-600/40'
                              : isDark
                              ? 'text-slate-300 hover:bg-slate-800/70 hover:text-white'
                              : 'text-slate-600 hover:bg-slate-100'
                          )}
                          disabled={!authToken}
                        >
                          {ratio}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label
                    className={combineClasses('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}
                  >
                    Ses Cinsiyeti
                  </label>
                  <div
                    className={combineClasses(
                      'mt-2 grid grid-cols-2 gap-2 rounded-2xl border p-2 transition-colors duration-200',
                      isDark ? 'border-slate-700/70 bg-slate-900/70' : 'border-slate-300 bg-white'
                    )}
                  >
                    {[
                      { code: 'male', label: 'Erkek' },
                      { code: 'female', label: 'Kadın' },
                    ].map((item) => {
                      const active = voiceGender === item.code;
                      return (
                        <button
                          key={item.code}
                          type="button"
                          onClick={() => {
                            setVoiceGender(item.code);
                            setSelectedVoice(VOICES_BY_GENDER[item.code][0]);
                          }}
                          className={combineClasses(
                            'rounded-xl px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60',
                            active
                              ? 'bg-purple-500 text-white shadow-lg shadow-purple-600/40'
                              : isDark
                              ? 'text-slate-300 hover:bg-slate-800/70 hover:text-white'
                              : 'text-slate-600 hover:bg-slate-100'
                          )}
                          disabled={!authToken}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label
                    className={combineClasses('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}
                  >
                    Ses Seçimi
                  </label>
                  <select
                    value={selectedVoice}
                    onChange={(event) => setSelectedVoice(event.target.value)}
                    className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
                    disabled={!authToken}
                  >
                    {VOICES_BY_GENDER[voiceGender].map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                onClick={autoGenerateTopic}
                className={combineClasses(
                  'w-full rounded-2xl border px-4 py-3 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60',
                  isDark
                    ? 'border-slate-700/70 text-slate-200 hover:border-blue-500/50 hover:text-white'
                    : 'border-slate-300 text-slate-700 hover:border-blue-400 hover:text-blue-600'
                )}
                disabled={!authToken || isGenerating || !topicConcept.trim()}
              >
                Trend Konu Bul
              </button>

              <input
                type="text"
                value={currentTopic}
                onChange={(event) => setCurrentTopic(event.target.value)}
                className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
                placeholder="Örn. 2025'te YouTube'da öne çıkan içerik fikirleri"
                disabled={!authToken}
              />

              <textarea
                value={scenario}
                onChange={(event) => { setScenario(event.target.value); setScenes(null); }}
                rows={10}
                className={`${textAreaClass} disabled:cursor-not-allowed disabled:opacity-60`}
                placeholder="Senaryo burada yer alacak..."
                disabled={!authToken}
              />

              <button
                onClick={generateScenario}
                className="w-full rounded-2xl bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-lg shadow-indigo-900/30 transition hover:shadow-purple-900/40 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={!authToken || !hasTopic || isGenerating}
              >
                Senaryo Oluştur
              </button>
            </div>

            <div className={combineClasses(panelClass, 'space-y-6 p-6')}>
              <h2 className={combineClasses('text-xl font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                Üretim Adımları
              </h2>

              <div className="grid gap-4">
                <button
                  onClick={createVisuals}
                  disabled={!authToken || !hasScenario || isCreatingVideo}
                  className={combineClasses(
                    'flex items-center justify-between rounded-2xl border px-4 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60',
                    isDark ? 'border-slate-700/70 bg-slate-900/70 hover:border-blue-500/40' : 'border-slate-300 bg-white hover:border-blue-400'
                  )}
                >
                  <div>
                    <p className={combineClasses('text-sm font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                      Görselleri Oluştur
                    </p>
                    <p className={combineClasses('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                      Senaryodan video tabanını hazırlar.
                    </p>
                  </div>
                  <span
                    className={combineClasses(
                      'rounded-xl px-3 py-1 text-xs font-medium',
                      isDark ? 'bg-blue-500/15 text-blue-100' : 'bg-blue-100 text-blue-600'
                    )}
                  >
                    {isCreatingVideo ? 'Hazırlanıyor...' : 'Başlat'}
                  </span>
                </button>

                <button
                  onClick={createVoice}
                  disabled={!authToken || !hasScenario || isCreatingVoice}
                  className={combineClasses(
                    'flex items-center justify-between rounded-2xl border px-4 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60',
                    isDark ? 'border-slate-700/70 bg-slate-900/70 hover:border-purple-500/40' : 'border-slate-300 bg-white hover:border-purple-400'
                  )}
                >
                  <div>
                    <p className={combineClasses('text-sm font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                      Seslendirme Oluştur
                    </p>
                    <p className={combineClasses('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                      Metninize uygun yapay ses üretir.
                    </p>
                  </div>
                  <span
                    className={combineClasses(
                      'rounded-xl px-3 py-1 text-xs font-medium',
                      isDark ? 'bg-purple-500/15 text-purple-100' : 'bg-purple-100 text-purple-600'
                    )}
                  >
                    {isCreatingVoice ? 'Hazırlanıyor...' : 'Başlat'}
                  </span>
                </button>

                <button
                  onClick={prepareFullVideo}
                  disabled={!authToken || isPreparingVideo || !videoPreview || !audioPreview}
                  className={combineClasses(
                    'flex items-center justify-between rounded-2xl border px-4 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60',
                    isDark ? 'border-slate-700/70 bg-slate-900/70 hover:border-emerald-500/40' : 'border-slate-300 bg-white hover:border-emerald-400'
                  )}
                >
                  <div>
                    <p className={combineClasses('text-sm font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                      Final Videoyu Hazırla
                    </p>
                    <p className={combineClasses('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                      Ses ve videoyu birleştirerek yayına hazırlar.
                    </p>
                  </div>
                  <span
                    className={combineClasses(
                      'rounded-xl px-3 py-1 text-xs font-medium',
                      isDark ? 'bg-emerald-500/15 text-emerald-100' : 'bg-emerald-100 text-emerald-600'
                    )}
                  >
                    {isPreparingVideo ? 'Hazırlanıyor...' : 'Hazırla'}
                  </span>
                </button>
              </div>

              <div className={combineClasses(mutedSurfaceClass, 'space-y-4 p-4')}>
                <h3 className={combineClasses('text-sm font-semibold', isDark ? 'text-slate-200' : 'text-slate-700')}>
                  {platformUploadLabels[platform]?.card || 'Platform Yüklemesi'}
                </h3>
                <input
                  type="text"
                  value={youtubeApiKey}
                  onChange={(event) => setYoutubeApiKey(event.target.value)}
                  className={`${combineClasses(
                    'w-full rounded-xl border px-4 py-3 text-xs transition focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-60',
                    isDark
                      ? 'border-slate-700/70 bg-slate-900/70 text-slate-200 focus:border-red-500 focus:ring-red-500/40'
                      : 'border-slate-300 bg-white text-slate-700 focus:border-red-400 focus:ring-red-400/30'
                  )}`}
                  placeholder="YouTube API anahtarınızı girin"
                  disabled={!authToken}
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    onClick={uploadToYouTube}
                    disabled={
                      !authToken ||
                      isGenerating ||
                      !(finalVideoPreview || videoPreview) ||
                      !hasAnyYoutubeKey
                    }
                    className="rounded-xl bg-gradient-to-r from-red-500 to-orange-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-red-900/40 transition hover:shadow-orange-900/40 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {platformUploadLabels[platform]?.button || "Platform'a Yükle"}
                  </button>
                  <div
                    className={combineClasses(
                      'rounded-xl px-4 py-3 text-xs transition',
                      isDark
                        ? 'border border-slate-700/70 bg-slate-900/70 text-slate-300'
                        : 'border border-slate-200 bg-white text-slate-500'
                    )}
                  >
                    Final videonuzu hazırladıktan sonra yüklemeyi başlatabilirsiniz. Kaydedilmiş bir YouTube anahtarınız varsa bu alanı boş bırakabilirsiniz.
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-2">
            <div className={combineClasses(panelClass, 'p-6')}>
              <h2 className={combineClasses('text-xl font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                Video Önizlemesi
              </h2>
              <p
                className={combineClasses(
                  'mt-2 text-xs',
                  isDark ? 'text-slate-400' : 'text-slate-500'
                )}
              >
                Oluşturulan varlıklar burada izlenebilir. Hazır değilse görsel alan boş görünür.
              </p>
              <div className="mt-6 space-y-4">
                <div className={combineClasses(mutedSurfaceClass, 'p-4')}>
                  <p className={combineClasses('text-sm font-semibold', isDark ? 'text-slate-200' : 'text-slate-700')}>
                    Görsel Önizlemesi
                  </p>
                  {videoPreview ? (
                    <video
                      key={videoPreview}
                      controls
                      className={combineClasses(
                        'mt-3 w-full rounded-xl border',
                        isDark ? 'border-slate-700/70' : 'border-slate-300'
                      )}
                      style={{
                        aspectRatio: aspectRatio === '16:9' ? '16/9' : '9/16',
                        maxHeight: '420px',
                      }}
                    >
                      <source src={videoPreview} type="video/mp4" />
                      Tarayıcınız video oynatmayı desteklemiyor.
                    </video>
                  ) : (
                    <div
                      className={combineClasses(
                        'mt-3 flex h-56 items-center justify-center rounded-xl border border-dashed text-sm',
                        isDark
                          ? 'border-slate-700/70 bg-slate-950/40 text-slate-500'
                          : 'border-slate-300 bg-slate-100 text-slate-500'
                      )}
                    >
                      Henüz bir video oluşturulmadı.
                    </div>
                  )}
                </div>

                <div className={combineClasses(mutedSurfaceClass, 'p-4')}>
                  <p className={combineClasses('text-sm font-semibold', isDark ? 'text-slate-200' : 'text-slate-700')}>
                    Final Video
                  </p>
                  {finalVideoPreview ? (
                    <div className="mt-3 space-y-3">
                      <video
                        key={finalVideoPreview}
                        controls
                        className={combineClasses(
                          'w-full rounded-xl border',
                          isDark ? 'border-slate-700/70' : 'border-slate-300'
                        )}
                        style={{
                          aspectRatio: aspectRatio === '16:9' ? '16/9' : '9/16',
                          maxHeight: '420px',
                        }}
                      >
                        <source src={finalVideoPreview} type="video/mp4" />
                        Tarayıcınız video oynatmayı desteklemiyor.
                      </video>
                      <button
                        onClick={() => window.open(finalVideoPreview, '_blank')}
                        className={combineClasses(
                          'w-full rounded-xl border px-4 py-2 text-sm font-semibold transition',
                          isDark
                            ? 'border-slate-700/70 text-slate-200 hover:border-emerald-500/40 hover:text-white'
                            : 'border-slate-300 text-slate-700 hover:border-emerald-400 hover:text-emerald-600'
                        )}
                      >
                        Videoyu İndir
                      </button>
                    </div>
                  ) : (
                    <div
                      className={combineClasses(
                        'mt-3 flex h-48 items-center justify-center rounded-xl border border-dashed text-sm',
                        isDark
                          ? 'border-slate-700/70 bg-slate-950/40 text-slate-500'
                          : 'border-slate-300 bg-slate-100 text-slate-500'
                      )}
                    >
                      Final video henüz oluşturulmadı.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={combineClasses(panelClass, 'p-6')}>
              <h2 className={combineClasses('text-xl font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                Ses Önizlemesi
              </h2>
              <p
                className={combineClasses(
                  'mt-2 text-xs',
                  isDark ? 'text-slate-400' : 'text-slate-500'
                )}
              >
                Üretilen ses dosyası burada dinlenebilir. Ses alanı boşsa henüz oluşturulmamıştır.
              </p>

              <div className={combineClasses(mutedSurfaceClass, 'mt-6 p-4')}>
                {audioPreview ? (
                  <audio key={audioPreview} controls className="w-full">
                    <source src={audioPreview} type="audio/mpeg" />
                    Tarayıcınız ses çalmayı desteklemiyor.
                  </audio>
                ) : (
                  <div
                    className={combineClasses(
                      'flex h-32 items-center justify-center rounded-xl border border-dashed text-sm',
                      isDark
                        ? 'border-slate-700/70 bg-slate-950/40 text-slate-500'
                        : 'border-slate-300 bg-slate-100 text-slate-500'
                    )}
                  >
                    Seslendirme henüz oluşturulmadı.
                  </div>
                )}
              </div>

              <div className={combineClasses(mutedSurfaceClass, 'mt-6 p-4')}>
                <h3 className={combineClasses('text-sm font-semibold', isDark ? 'text-slate-200' : 'text-slate-700')}>
                  Senaryo Özeti
                </h3>
                <p
                  className={combineClasses(
                    'mt-2 text-sm',
                    isDark ? 'text-slate-400' : 'text-slate-600'
                  )}
                >
                  {scenario
                    ? scenario.split('\n').slice(0, 3).join(' ')
                    : 'Senaryo oluştuğunda burada kısa bir özet yer alacak.'}
                </p>
              </div>
            </div>
          </section>

          {(() => {
            const activeJobId = readStorage(STORAGE_VISUAL_JOB_KEY) || readStorage(STORAGE_AUTOMATION_JOB_KEY);
            const isActive = !!activeJobId && (isCreatingVideo || isGenerating);
            const activeRow = isActive ? {
              id: `active-${activeJobId}`,
              _active: true,
              topic: currentTopic || 'Yeni Üretim',
              platform,
              status: 'running',
              created_at: new Date().toISOString(),
              progress: generationStatus || 'Hazırlanıyor...',
            } : null;
            const mergedGenerations = activeRow ? [activeRow, ...recentGenerations] : recentGenerations;
            return (
          <section className={combineClasses(panelClass, 'p-6')}>
            <div className="flex items-center justify-between">
              <h2 className={combineClasses('text-xl font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                Son Üretimler
              </h2>
              <span
                className={combineClasses(
                  'rounded-xl px-3 py-1 text-xs font-medium',
                  isDark ? 'border border-slate-700/70 text-slate-300' : 'border border-slate-300 text-slate-600'
                )}
              >
                {mergedGenerations.length} kayıt
              </span>
            </div>

            {mergedGenerations.length > 0 ? (
              <div
                className={combineClasses(
                  'mt-6 overflow-hidden rounded-2xl border',
                  isDark ? 'border-slate-700/70' : 'border-slate-200'
                )}
              >
                <table className="w-full table-fixed border-collapse text-left text-sm">
                  <thead
                    className={combineClasses(
                      'text-xs uppercase tracking-wide',
                      isDark ? 'bg-slate-950/70 text-slate-400' : 'bg-slate-100 text-slate-600'
                    )}
                  >
                    <tr>
                      <th className="px-4 py-3">Konu</th>
                      <th className="px-4 py-3">Platform</th>
                      <th className="px-4 py-3">Durum</th>
                      <th className="px-4 py-3">Oluşturma</th>
                      <th className="px-4 py-3">Bağlantı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mergedGenerations.map((generation) => {
                      const clickable = generation._active || generation.status === 'completed';
                      return (
                      <tr
                        key={generation.id}
                        onClick={clickable && !generation._active ? () => loadGenerationToForm(generation) : undefined}
                        className={combineClasses(
                          'border-t text-sm',
                          clickable && !generation._active ? 'cursor-pointer' : '',
                          generation._active
                            ? isDark ? 'bg-yellow-500/5' : 'bg-yellow-50'
                            : '',
                          isDark
                            ? 'border-slate-800/60 text-slate-300 hover:bg-slate-800/40'
                            : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                        )}
                      >
                        <td className={combineClasses('px-4 py-3 font-medium', isDark ? 'text-slate-200' : 'text-slate-800')}>
                          {generation._active && <span className="mr-2 inline-block animate-pulse">●</span>}
                          {generation.topic}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {platformOptions.find((option) => option.code === generation.platform)?.label || (generation.platform || 'YouTube')}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={combineClasses(
                              'inline-flex rounded-full px-3 py-1 text-xs font-semibold',
                              generation._active
                                ? isDark ? 'bg-yellow-500/20 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
                                : generation.status === 'completed'
                                ? isDark
                                  ? 'bg-emerald-500/15 text-emerald-200'
                                  : 'bg-emerald-100 text-emerald-700'
                                : generation.status === 'failed'
                                ? isDark
                                  ? 'bg-red-500/15 text-red-200'
                                  : 'bg-red-100 text-red-600'
                                : isDark
                                ? 'bg-yellow-500/15 text-yellow-200'
                                : 'bg-yellow-100 text-yellow-700'
                            )}
                          >
                            {generation._active ? 'Hazırlanıyor' : generation.status}
                          </span>
                        </td>
                        <td className={combineClasses('px-4 py-3 text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                          {generation._active
                            ? <span className="italic">{generation.progress}</span>
                            : new Date(generation.created_at).toLocaleDateString('tr-TR')}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {generation.youtube_url ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); window.open(generation.youtube_url, '_blank'); }}
                              className={combineClasses(
                                'rounded-full border px-3 py-1 text-xs font-medium transition',
                                isDark
                                  ? 'border-blue-500/30 text-blue-200 hover:border-blue-500 hover:text-white'
                                  : 'border-blue-300 text-blue-600 hover:border-blue-500 hover:text-blue-700'
                              )}
                            >
                              İzle
                            </button>
                          ) : generation._active ? (
                            <span className={combineClasses('italic', isDark ? 'text-yellow-300' : 'text-yellow-700')}>İşleniyor</span>
                          ) : clickable ? (
                            <span className={combineClasses(isDark ? 'text-slate-400' : 'text-slate-500')}>Forma yükle →</span>
                          ) : (
                            <span className={combineClasses(isDark ? 'text-slate-400' : 'text-slate-500')}>Yüklenmedi</span>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                className={combineClasses(
                  mutedSurfaceClass,
                  'mt-6 border-dashed p-10 text-center text-sm',
                  isDark ? 'text-slate-400' : 'text-slate-500'
                )}
              >
                Henüz üretim kaydı bulunmuyor. Bir senaryo oluşturup otomasyonu başlatın.
              </div>
            )}
          </section>
            );
          })()}
          </>
          )}

          {activeTab === 'presets' && (
            <div className="space-y-6">
              <div className={combineClasses(panelClass, 'space-y-4 p-6')}>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="grow">
                    <h2 className={combineClasses('text-xl font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                      Yeni Ayar Kaydet
                    </h2>
                    <p className={combineClasses('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                      Mevcut platform ve üretim ayarlarınızı isimlendirip kaydedin.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <input
                      type="text"
                      value={presetName}
                      onChange={(event) => setPresetName(event.target.value)}
                      className={combineClasses(
                        'rounded-xl border px-4 py-3 text-sm transition focus:outline-none focus:ring-2',
                        isDark
                          ? 'border-slate-700/70 bg-slate-900/70 text-slate-200 focus:border-purple-500 focus:ring-purple-500/40'
                          : 'border-slate-300 bg-white text-slate-700 focus:border-purple-500 focus:ring-purple-500/30'
                      )}
                      placeholder="Örn. TikTok İngilizce 2dk"
                    />
                    <button
                      type="button"
                      onClick={handlePresetSave}
                      disabled={isSavingPreset || !presetName.trim() || isGenerating}
                      className="rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-600/30 transition hover:shadow-emerald-700/40 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSavingPreset ? 'Kaydediliyor...' : 'Ayarı Kaydet'}
                    </button>
                  </div>
                </div>
                <div className={combineClasses('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                  <span className="font-medium">Seçim:</span>{' '}
                  {platformOptions.find((item) => item.code === platform)?.label || 'YouTube'} •{' '}
                  {(Number(videoLengthMinutes) || 1)} dk • {aspectRatio} •{' '}
                  {language?.toLowerCase().startsWith('en') ? 'English' : 'Türkçe'}
                </div>
              </div>

              <div className={combineClasses(panelClass, 'space-y-4 p-6')}>
                <div className="flex items-center justify-between">
                  <h2 className={combineClasses('text-xl font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                    Kayıtlı Ayarlar
                  </h2>
                  <span
                    className={combineClasses(
                      'rounded-xl px-3 py-1 text-xs font-medium',
                      isDark ? 'border border-slate-700/70 text-slate-300' : 'border border-slate-300 text-slate-600'
                    )}
                  >
                    {presets.length} kayıt
                  </span>
                </div>

                {isLoadingPresets ? (
                  <div className={combineClasses('rounded-2xl border border-dashed px-4 py-10 text-center text-sm', isDark ? 'border-slate-700/70 text-slate-400' : 'border-slate-300 text-slate-500')}>
                    Ayarlar yükleniyor...
                  </div>
                ) : presets.length > 0 ? (
                  <div
                    className={combineClasses(
                      'overflow-hidden rounded-2xl border',
                      isDark ? 'border-slate-700/70' : 'border-slate-200'
                    )}
                  >
                    <table className="w-full table-fixed border-collapse text-sm">
                      <thead
                        className={combineClasses(
                          'text-xs uppercase tracking-wide',
                          isDark ? 'bg-slate-950/70 text-slate-400' : 'bg-slate-100 text-slate-600'
                        )}
                      >
                        <tr>
                          <th className="px-4 py-3">Ad</th>
                          <th className="px-4 py-3">Platform</th>
                          <th className="px-4 py-3">Süre</th>
                          <th className="px-4 py-3">Dil</th>
                          <th className="px-4 py-3">Format</th>
                          <th className="px-4 py-3">Oluşturma</th>
                          <th className="px-4 py-3">İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {presets.map((preset) => {
                          const languageLabel = (preset.language || '').toLowerCase().startsWith('en') ? 'English' : 'Türkçe';
                          const platformLabel = platformOptions.find((item) => item.code === preset.platform)?.label || preset.platform;
                          return (
                            <tr
                              key={preset.id}
                              className={combineClasses(
                                'border-t',
                                isDark ? 'border-slate-800/60 text-slate-300' : 'border-slate-200 text-slate-700'
                              )}
                            >
                              <td className={combineClasses('px-4 py-3 font-medium', isDark ? 'text-slate-200' : 'text-slate-800')}>
                                {preset.name}
                              </td>
                              <td className="px-4 py-3 text-xs">{platformLabel}</td>
                              <td className="px-4 py-3 text-xs">{Number(preset.duration_minutes || 0).toFixed(1)} dk</td>
                              <td className="px-4 py-3 text-xs">{languageLabel}</td>
                              <td className="px-4 py-3 text-xs">{preset.aspect_ratio}</td>
                              <td className="px-4 py-3 text-xs">
                                {preset.created_at
                                  ? new Date(preset.created_at).toLocaleString('tr-TR')
                                  : '—'}
                              </td>
                              <td className="px-4 py-3 text-xs">
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handlePresetRun(preset)}
                                    disabled={isGenerating}
                                    className="rounded-full bg-gradient-to-r from-blue-500 to-purple-500 px-3 py-1 text-xs font-semibold text-white shadow-lg shadow-blue-500/30 transition hover:shadow-purple-600/40 disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    Çalıştır
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handlePresetDelete(preset.id)}
                                    disabled={isGenerating}
                                    className={combineClasses(
                                      'rounded-full px-3 py-1 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60',
                                      isDark
                                        ? 'border border-slate-700/70 text-slate-300 hover:bg-slate-800/70'
                                        : 'border border-slate-300 text-slate-600 hover:bg-slate-100'
                                    )}
                                  >
                                    Sil
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div
                    className={combineClasses(
                      mutedSurfaceClass,
                      'border-dashed p-10 text-center text-sm',
                      isDark ? 'text-slate-400' : 'text-slate-500'
                    )}
                  >
                    Henüz kaydedilmiş ayar bulunmuyor. Mevcut konfigürasyonu kaydederek hızlıca erişebilirsiniz.
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'account' && (
            <div className="space-y-6">
              <div className={combineClasses(panelClass, 'space-y-6 p-6')}>
                <div>
                  <h2 className={combineClasses('text-xl font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                    Hesap Ayarları
                  </h2>
                  <p className={combineClasses('mt-2 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Platform API anahtarlarınızı güvenle saklayın. Anahtarlar yalnızca sizin hesabınıza bağlı otomasyonlarda kullanılır.
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {platformOptions.map((item) => (
                    <div key={item.code} className={combineClasses(mutedSurfaceClass, 'space-y-2 p-4')}>
                      <p className={combineClasses('text-sm font-semibold', isDark ? 'text-slate-200' : 'text-slate-700')}>
                        {item.label}
                      </p>
                      <input
                        type="text"
                        value={apiKeys[item.code] || ''}
                        onChange={(event) =>
                          setApiKeys((previous) => ({
                            ...previous,
                            [item.code]: event.target.value,
                          }))
                        }
                        className={combineClasses(
                          'w-full rounded-xl border px-4 py-3 text-sm transition focus:outline-none focus:ring-2',
                          isDark
                            ? 'border-slate-700/70 bg-slate-900/70 text-slate-200 focus:border-emerald-500 focus:ring-emerald-500/40'
                            : 'border-slate-300 bg-white text-slate-700 focus:border-emerald-500 focus:ring-emerald-500/30'
                        )}
                        placeholder={`${item.label} API anahtarı`}
                      />
                      <p className={combineClasses('text-xs', isDark ? 'text-slate-500' : 'text-slate-500')}>
                        Boş bırakırsanız anahtar silinir.
                      </p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSaveApiKeys}
                    disabled={isSavingApiKeys}
                    className="rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/40 transition hover:shadow-indigo-700/40 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSavingApiKeys ? 'Kaydediliyor...' : 'Anahtarları Kaydet'}
                  </button>
                  <span className={combineClasses('text-xs', isDark ? 'text-slate-500' : 'text-slate-500')}>
                    Anahtarlarınız şifrelenmiş olarak saklanır ve yalnızca sizin işlemlerinizde kullanılır.
                  </span>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
